'use client';

/* ──────────────────────────────────────────────
 *  StepFooter — footer d'action possédé par le shell.
 *  Adaptateur : projette le contexte de réservation
 *  (`useBooking`, `useStepNav`) sur la barre partagée
 *  `TunnelFooterBar` (mutualisée avec les tunnels carte
 *  cadeau et course). Position de l'action invariante
 *  quelle que soit l'étape.
 *
 *  C'est aussi l'unique récap en colonne unique : le
 *  bloc total/compteur déplie une feuille (détail des
 *  lignes, suppléments, retrait) juste au-dessus de la
 *  barre.
 * ────────────────────────────────────────────── */

import type { JSX } from 'react';
import { useBooking } from '../context/BookingContext';
import { useBookingCodes } from '../context/BookingCodesContext';
import { useStepNav } from '../context/StepNavContext';
import { computeSummaryStruckTotal, computeSummaryTotalValue } from '../utils/summary-total';
import { BookingSummary } from './BookingSummary';
import { TunnelFooterBar } from './shared/TunnelFooterBar';

/**
 * Clé de fermeture de la feuille récap : elle se referme au changement d'étape ET à l'ouverture du paiement
 * (décision 6 du plan de l'étape 5). La pré-réservation créée, le bloc paiement apparaît sous le formulaire, sur
 * la même étape : une feuille restée ouverte le couvrirait. Une pré-réservation recréée (code posé depuis la
 * feuille) garde la clé : la feuille ne se ferme pas sous les doigts du client.
 */
export const footerSheetKey = (step: string, preReservationUuid: string | undefined): string =>
  preReservationUuid ? `${step}:payment` : step;

export function StepFooter(): JSX.Element | null {
  const { ctx, labels, currentStep } = useBooking();
  const { amounts, summaryError } = useBookingCodes();
  const nav = useStepNav();

  // Demande de devis : hors parcours, le panier n'y a rien à faire (le formulaire porte son bouton).
  if (
    !nav ||
    currentStep === 'success' ||
    currentStep === 'loading' ||
    currentStep === 'error' ||
    currentStep === 'quote'
  ) {
    return null;
  }

  return (
    <TunnelFooterBar
      onBack={nav.onBack}
      primary={nav.ownsPrimary ? undefined : nav.primary}
      // Total après remise (avant bons), le prix normal barré à côté : comme la ligne « Total » du récap. Résumé du
      // panier en échec : pas de total périmé, la feuille propose « Réessayer ».
      totalValue={computeSummaryTotalValue(ctx.cartSummary, ctx.cartSummaryLoading, labels, amounts, summaryError)}
      totalStruck={computeSummaryStruckTotal(amounts, summaryError)}
      count={ctx.cartItems.length}
      sheetTitle={ctx.entryMode === 'multi' ? labels.selection_title : labels.summary_title_single}
      sheet={<BookingSummary variant="sheet" />}
      stepKey={footerSheetKey(currentStep, ctx.preReservationUuid)}
      labels={{
        back: labels.back,
        close: labels.close,
        show_detail: labels.show_detail,
        hide_detail: labels.hide_detail,
        item_singular: labels.prestation_singular,
        item_plural: labels.prestations_plural,
      }}
    />
  );
}
